/**
 * RequireAuth — shared auth gate.
 *
 * Replaces hand-rolled `RequireAdmin` / `RequireAuth` shells in each frontend
 * app. Provides the same three-state flow:
 *
 *   1. Auth still loading → centered spinner
 *   2. Not signed in → caller-provided redirect (default `<Navigate to={loginPath}/>`)
 *   3. Signed in but missing required role → "Access denied" with sign-out + support
 *   4. Otherwise → children
 *
 * Role check: currently only `'admin'` (uses the `isAdmin` derived flag from
 * `@inferrex/ui/auth`). When the auth context grows a `roles[]` array this
 * primitive can grow to match without callers changing.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './generated/index.js';

export interface RequireAuthProps {
  readonly children: ReactNode;
  /** Required role. Currently 'admin' is the only enforceable value. */
  readonly requiredRole?: 'admin';
  /** Where to redirect when no user. Defaults to '/login'. */
  readonly loginPath?: string;
  /** Subject line / body when the user clicks "Contact support". */
  readonly supportEmail?: string;
  /** Override the access-denied heading. */
  readonly accessDeniedTitle?: string;
  /** Override the access-denied body (use {email} placeholder). */
  readonly accessDeniedBody?: (email: string) => ReactNode;
  /** Custom fallback while auth is initialising. */
  readonly loadingFallback?: ReactNode;
}

export function RequireAuth({
  children,
  requiredRole,
  loginPath = '/login',
  supportEmail = 'support@inferrex.com',
  accessDeniedTitle = 'Access denied',
  accessDeniedBody,
  loadingFallback,
}: RequireAuthProps) {
  const { user, isAdmin, loading, signOut } = useAuth();

  if (loading) {
    return (
      <>{loadingFallback ?? (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}</>
    );
  }

  if (!user) {
    return <Navigate to={loginPath} replace />;
  }

  const missingRole = requiredRole === 'admin' && !isAdmin;
  if (missingRole) {
    const handleSignOut = () => { void signOut(); };
    const email = user.email ?? 'this account';
    const supportSubject = requiredRole === 'admin' ? 'Admin access request' : 'Access request';
    const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent(supportSubject)}`;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center space-y-4 max-w-md">
          <h1 className="text-2xl font-semibold text-foreground">{accessDeniedTitle}</h1>
          <div className="text-muted-foreground">
            {accessDeniedBody
              ? accessDeniedBody(email)
              : (
                <p>
                  The account <span className="font-medium text-foreground">{email}</span>{' '}
                  doesn't have {requiredRole} privileges. Ask an existing {requiredRole} to grant
                  you access, or sign in with a different account.
                </p>
              )}
          </div>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleSignOut}
              className="px-4 py-2 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted cursor-pointer bg-transparent"
            >
              Sign out
            </button>
            <a
              href={mailto}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 no-underline"
            >
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
